// ABOUTME: Full-screen blocking modal that prevents access to app when subscription is expired or payment failed
// ABOUTME: Displays appropriate message and actions based on lockout reason, only allows access to MyFamilyPage

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
} from '@mui/material';
import { Warning, CreditCard, Lock } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

export type LockoutReason = 'trial_expired' | 'payment_failed' | 'subscription_cancelled' | 'admin_cancelled' | 'refunded' | 'emergency_lockout';

interface SubscriptionBlockingModalProps {
  open: boolean;
  lockoutReason: LockoutReason;
  onManageSubscription: () => void;
  onLogout: () => void;
}

const SubscriptionBlockingModal: React.FC<SubscriptionBlockingModalProps> = ({
  open,
  lockoutReason,
  onManageSubscription,
  onLogout,
}) => {
  const theme = useTheme();
  const isEmergencyLockout = lockoutReason === 'emergency_lockout';
  const isPaymentFailed = lockoutReason === 'payment_failed';
  const isSubscriptionCancelled = lockoutReason === 'subscription_cancelled';
  const isAdminCancelled = lockoutReason === 'admin_cancelled';
  const isRefunded = lockoutReason === 'refunded';

  let title = 'Trial Expired';
  let message = 'Your 14-day trial has expired';
  let explanation = 'A subscription is required to continue using Let\'s Reset. Please subscribe to regain access to all features.';
  let actionButtonText = 'Manage Subscription';
  let helpText = 'You can access the My Family page to manage your subscription, or log out.';

  if (isEmergencyLockout) {
    title = 'Account Locked';
    message = 'Your account has been locked by an administrator';
    explanation = 'Access to this account has been suspended. If you believe this is an error, please contact support at support@letsreset.ai.';
    actionButtonText = 'Contact Support';
    helpText = 'Please contact support to resolve this issue, or log out.';
  } else if (isPaymentFailed) {
    title = 'Payment Failed';
    message = 'We were unable to process your payment';
    explanation = 'Please update your payment method to continue using Let\'s Reset.';
    actionButtonText = 'Update Payment Method';
  } else if (isSubscriptionCancelled) {
    title = 'Subscription Expired';
    message = 'Your subscription access period has ended';
    explanation = 'Your subscription was cancelled and the grace period has expired. Please resubscribe to regain access to all features.';
    actionButtonText = 'Resubscribe';
  } else if (isAdminCancelled) {
    title = 'Account Suspended';
    message = 'Your subscription has been cancelled by an administrator';
    explanation = 'Please contact support or resubscribe to regain access to Let\'s Reset.';
    actionButtonText = 'Manage Subscription';
  } else if (isRefunded) {
    title = 'Subscription Refunded';
    message = 'Your subscription has been refunded';
    explanation = 'We\'ve processed your refund and your subscription is now cancelled. If this was a mistake or you\'d like to resubscribe, please contact us at support@letsreset.ai.';
    actionButtonText = 'Subscribe Again';
  }

  const Icon = isEmergencyLockout ? Lock : isPaymentFailed ? CreditCard : Warning;
  const iconColor = isEmergencyLockout ? theme.palette.error.dark : isPaymentFailed ? theme.palette.warning.main : theme.palette.error.main;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="subscription-blocking-modal-title"
      aria-describedby="subscription-blocking-modal-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'visible',
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: alpha(theme.palette.common.black, 0.8),
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Icon sx={{ fontSize: 56, color: iconColor }} />
        </Box>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 4, pb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {message}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {explanation}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: 'action.hover',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {helpText}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', px: 4, pb: 4, gap: 2, flexDirection: 'column' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={isEmergencyLockout
            ? () => window.location.href = 'mailto:support@letsreset.ai?subject=Account%20Locked'
            : onManageSubscription}
          sx={{
            py: 1.5,
            fontWeight: 600,
          }}
        >
          {actionButtonText}
        </Button>

        <Button
          variant="text"
          color="inherit"
          size="medium"
          onClick={onLogout}
          sx={{
            textTransform: 'none',
          }}
        >
          Log Out
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionBlockingModal;
